import { useState } from 'react';
import { isoVersDate } from '../../api/mappers';
import { Card } from '../../components/ui/Card.jsx';
import { FRAIS_CAT } from '../../constants/accounting';
import {
  categorieDuMouvement,
  commerceDuLibelle,
  achatALEtranger,
  estUnRetrait,
  nomEtLieu,
  natureDuMouvement,
} from '../../lib/categorie-releve';
import { periodesComptable } from '../../lib/cloture';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { jourIso, rapprocheSorties } from '../../lib/rapprochement-sorties';
import { propositionsEntree, rapprocheEntrees } from '../../lib/rapprochement-entrees';
import { cleMouvement, noteDuSens, sensConnu } from '../../lib/releve-bancaire';
import { motsDuNom } from '../../lib/pieces-depenses';
import { nomComplet } from '../../lib/acces';
import { C } from '../../theme/palette';

const petit = { fontSize: 9, marginTop: 4, lineHeight: 1.4 };
const bouton = {
  border: 'none',
  borderRadius: 7,
  padding: '6px 9px',
  fontSize: 9.5,
  fontWeight: 800,
  cursor: 'pointer',
  marginTop: 5,
};
const lienDiscret = {
  border: 'none',
  background: 'none',
  padding: 0,
  marginLeft: 6,
  fontSize: 9,
  fontWeight: 700,
  color: C.muted,
  textDecoration: 'underline',
  cursor: 'pointer',
};

/** Libellé d'une période du comptable : « 3e trimestre 2026 ». */
function libellePeriode(freq, periode, annee) {
  const p = periodesComptable(freq).find(([k]) => k === periode);
  return (p ? tr(p[1]) : periode) + ' ' + annee;
}

const francs = (n) => DEVISE + ' ' + fmt(n);

/** La nature d'un mouvement, dite au patron : « Carte », « TWINT », « Virement reçu »… */
function libelleNature(t) {
  switch (natureDuMouvement(t)) {
    case 'carte':
      return tr('Carte');
    case 'twint':
      return 'TWINT';
    case 'lsv':
      return 'LSV';
    case 'permanent':
      return tr('Ordre permanent');
    case 'retrait':
      return tr('Retrait d’espèces');
    case 'frais':
      return tr('Frais bancaires');
    case 'recu':
      return tr('Virement reçu');
    case 'virement':
      return tr('Virement bancaire');
    default:
      return tr('À vérifier');
  }
}

/**
 * Les mouvements du relevé bancaire, chaque sortie reliée à ce qu'elle paie.
 *
 * <p>Le relevé s'affichait sans rien relier. Le paiement du comptable par
 * bulletin de versement se pointe maintenant d'un geste (ou tout seul à
 * l'import), une facture fournisseur déjà saisie sur un chantier est
 * reconnue, et une sortie inconnue devient une dépense sans la ressaisir.
 *
 * <p>Pour l'argent reçu, la ligne dit qui a payé (« 👤 Sylvie
 * Chevrot-Leblond ») et propose, choisie d'avance quand la référence ou le
 * montant la désigne, sa facture à payer ou l'acompte de son offre : le patron
 * confirme d'un geste. Une facture faite dans un autre logiciel, ou une sortie
 * déjà comptée ou privée, se marque sans rien créer, et se démarque.
 *
 * @param {object} props propriétés
 * @param {object[]} props.mouvements lignes du relevé
 * @param {object} [props.comptable] fiche du comptable
 * @param {object[]} [props.chantiers] chantiers, pour leurs factures fournisseurs
 * @param {object[]} [props.frais] dépenses déjà saisies
 * @param {object[]} [props.achats] factures de fournisseurs et de sous-traitants
 *   jointes aux offres et aux factures : déjà comptées, jamais proposées en dépense
 * @param {(annee: number, periode: string) => void} props.onPointeComptable pointe un règlement
 * @param {(depense: object) => void} [props.onAjouteDepense] enregistre une dépense
 * @param {object[]} [props.employes] équipe : leurs salaires versés sont déjà comptés
 * @param {object[]} [props.factures] factures clients, pour relier l'argent reçu
 * @param {object[]} [props.clients] clients, pour leur nom
 * @param {object[]} [props.devis] offres, pour leurs acomptes
 * @param {(id: *, montant: number, date: string) => void} [props.onPaiement] enregistre un paiement reçu
 * @param {(offre: object, montant: number, date: string) => Promise<*>} [props.onAcompte]
 *   établit (ou retrouve) la facture d'acompte de l'offre et y enregistre le
 *   paiement ; rend l'identifiant de la facture, ou null en cas d'échec
 * @param {(cle: string, note: object) => void} [props.onNote] note sur un
 *   mouvement du relevé gardé ce que le patron en a dit
 * @param {(id: *) => void} [props.onOuvreOffre] ouvre une offre
 * @param {(cle: string) => void} [props.onRetire] retire du relevé gardé une
 *   ligne que le patron dit en double (déjà lue dans un autre relevé)
 * @returns {import('react').ReactElement} la liste des mouvements
 */
export function MouvementsReleve({
  mouvements,
  comptable,
  chantiers,
  frais,
  achats,
  onPointeComptable,
  onAjouteDepense,
  employes,
  factures,
  clients,
  devis,
  onPaiement,
  onAcompte,
  onNote,
  onOuvreOffre,
  onRetire,
}) {
  // La catégorie choisie, par mouvement (sa clé) : choisie par sa place, elle
  // passait à la ligne voisine dès qu'un relevé s'ajoutait.
  const [categorie, setCategorie] = useState({});
  const [choix, setChoix] = useState({});
  // Les paiements reliés d'ici : le libellé ne nomme pas toujours le client.
  // Ils sont aussi notés sur le relevé gardé (`factureId`), pour ne pas être
  // proposés une seconde fois au retour sur l'écran.
  const [lies, setLies] = useState({});
  const [enCours, setEnCours] = useState(null);
  const cle = cleMouvement;
  // Les dépenses de A à Z, les paiements à l'État en dernier.
  const categoriesTriees = Object.entries(FRAIS_CAT).sort(
    ([, a], [, b]) => !!a.etat - !!b.etat || tr(a.lib).localeCompare(tr(b.lib)),
  );
  // La catégorie choisie, sinon celle que le libellé ou une dépense déjà
  // rangée chez le même commerçant propose ; '' : au patron de choisir.
  const categorieDe = (t) => categorie[cle(t)] ?? categorieDuMouvement(t.label, frais);
  // Une ligne peut-être en double (lue dans un autre relevé) ne se relie à
  // rien tant que le patron n'a pas tranché : elle prenait la dépense ou la
  // facture de la vraie.
  const enAttente = (t) => !!t.doublonPossible || !sensConnu(t);
  // Le salaire versé à un employé (son nom, ou « salaire », dans le libellé)
  // est déjà compté par l'Équipe, charges comprises : proposé en dépense, il
  // comptait deux fois — 36 lignes de salaires sur un relevé d'un an.
  const salarieDe = (t) => {
    if (t.sens !== 'DBIT') return null;
    const mots = motsDuNom(t.label);
    const employe = (employes || []).find((e) => {
      const nom = motsDuNom(nomComplet(e));
      return nom.length > 0 && nom.every((m) => mots.includes(m));
    });
    if (employe) return nomComplet(employe);
    return /\b(salaires?|lohn|gehalt|stipendio)\b/i.test(String(t.label || '')) ? '' : null;
  };
  const liens = rapprocheSorties(
    mouvements.map((t) => (enAttente(t) ? { ...t, sens: 'attente' } : t)),
    { comptable, chantiers, frais, achats },
  ).map((l, i) => {
    if (l && !l.propose) return l;
    const t = mouvements[i];
    if (enAttente(t)) return l;
    const nom = salarieDe(t);
    return nom == null ? l : { genre: 'salaire', nom };
  });
  const lieA = (t) => lies[cle(t)] ?? t.factureId;
  const factureLiee = (t) => {
    const id = lieA(t);
    return id != null ? (factures || []).find((x) => String(x.id) === String(id)) : null;
  };
  // L'argent reçu, relié à sa facture : celles déjà payées comprises, pour
  // dire « payée » sur la ligne une fois le paiement enregistré. Une ligne
  // déjà réglée par le patron (autre logiciel, facture choisie) n'en prend
  // aucune à une autre.
  const auto = rapprocheEntrees(
    mouvements.map((t) =>
      t.traite || enAttente(t) || lieA(t) != null ? { ...t, sens: 'traite' } : t,
    ),
    factures,
    clients,
    { payeesAussi: true },
  );
  const entrees = mouvements.map((t, i) => {
    const f = factureLiee(t);
    return f ? { facture: f, par: 'main' } : auto[i];
  });
  const nomClient = (id) => (clients || []).find((c) => c.id === id)?.nom || '';
  // Les acomptes des offres ne se proposent que si l'écran sait établir leur
  // facture (rôle qui voit les offres et encaisse).
  const offres = onAcompte ? devis : [];
  const lie = (t, id) => {
    setLies((p) => ({ ...p, [cle(t)]: id }));
    if (onNote) onNote(cle(t), { factureId: id });
  };
  const jour = (t) => isoVersDate(jourIso(t.date));

  /** Le choix d'une ligne d'argent reçu : qui a payé, et quoi. */
  const lignePaiement = (t) => {
    const p = propositionsEntree(t, { factures, clients, devis: offres });
    const rien = !!p.client?.nom && !p.factures.length && !p.acomptes.length;
    const defaut = p.propose ? (p.propose.genre === 'facture' ? 'f:' : 'a:') + p.propose.id : '';
    const valeur = choix[cle(t)] ?? defaut;
    const offreChoisie = valeur.startsWith('a:')
      ? [...p.acomptes, ...p.autresAcomptes].find((a) => 'a:' + a.offre.id === valeur)?.offre
      : null;
    const autresOptions = [
      ...p.autres.map(({ facture: f, reste }) => (
        <option key={'f' + f.id} value={'f:' + f.id}>
          {trf('n° {0} · {1} · {2}', f.num || f.id, nomClient(f.clientId), fmt(reste))}
        </option>
      )),
      ...p.autresAcomptes.map(({ offre: o, reste }) => (
        <option key={'a' + o.id} value={'a:' + o.id}>
          {trf(
            '💰 Acompte de l’offre n° {0} · {1} · {2}',
            o.num || o.id,
            nomClient(o.clientId),
            fmt(reste),
          )}
        </option>
      )),
    ];
    const aChoisir = p.factures.length + p.acomptes.length + autresOptions.length > 0;
    const ailleurs = onNote && (
      <button
        type="button"
        style={
          rien
            ? { ...bouton, background: C.tealL, color: C.teal }
            : { ...bouton, background: 'none', color: C.muted, border: '1px solid ' + C.border }
        }
        onClick={() => onNote(cle(t), { traite: 'ailleurs' })}
      >
        {tr('📁 Facture faite dans un autre logiciel')}
      </button>
    );
    const confirme = async () => {
      if (valeur.startsWith('f:')) {
        const f = (factures || []).find((x) => 'f:' + x.id === valeur);
        if (!f) return;
        onPaiement(f.id, +t.montant || 0, jour(t));
        lie(t, f.id);
      } else if (offreChoisie && onAcompte) {
        setEnCours(cle(t));
        try {
          const id = await onAcompte(offreChoisie, +t.montant || 0, jour(t));
          if (id != null) lie(t, id);
        } finally {
          setEnCours(null);
        }
      }
    };
    const occupe = enCours === cle(t);
    return (
      <>
        {p.client?.nom && (
          <div style={{ ...petit, color: C.ink, fontWeight: 700 }}>{'👤 ' + p.client.nom}</div>
        )}
        {/* Un client connu sans rien à payer ici : sa facture a sans doute été
            faite dans l'ancien logiciel. Proposé en premier, jamais appliqué
            sans le geste du patron. */}
        {rien && ailleurs}
        {rien && (
          <div style={{ fontSize: 9, color: C.orange, marginTop: 4, lineHeight: 1.4 }}>
            {trf(
              'Aucune facture à payer au nom de {0}. Un acompte ? Ouvre son offre et touche « générer la facture » de l’acompte, puis reviens ici.',
              p.client.nom,
            )}
            {onOuvreOffre &&
              p.offresSansAcompte.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  style={lienDiscret}
                  onClick={() => onOuvreOffre(o.id)}
                >
                  {trf('Ouvrir l’offre n° {0}', o.num || o.id)}
                </button>
              ))}
          </div>
        )}
        {aChoisir && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              aria-label={tr('Facture payée par ce versement')}
              value={valeur}
              onChange={(e) => setChoix((c) => ({ ...c, [cle(t)]: e.target.value }))}
              style={{
                marginTop: 5,
                border: '1px solid ' + C.border,
                borderRadius: 7,
                padding: '5px 6px',
                fontSize: 9.5,
                maxWidth: '62%',
              }}
            >
              <option value="">{tr('C’est le paiement de…')}</option>
              {p.factures.map(({ facture: f, reste }) => (
                <option key={'f' + f.id} value={'f:' + f.id}>
                  {trf('🧾 Facture n° {0} — reste {1}', f.num || f.id, francs(reste))}
                </option>
              ))}
              {p.acomptes.map(({ offre: o, reste }) => (
                <option key={'a' + o.id} value={'a:' + o.id}>
                  {trf('💰 Acompte de l’offre n° {0} — {1}', o.num || o.id, francs(reste))}
                </option>
              ))}
              {/* Les pièces des autres clients restent à portée, sous un
                  séparateur, jamais choisies d'avance. */}
              {p.client && autresOptions.length > 0 ? (
                <optgroup label={tr('Autres clients')}>{autresOptions}</optgroup>
              ) : (
                autresOptions
              )}
            </select>
            <button
              type="button"
              disabled={!valeur || occupe}
              style={{
                ...bouton,
                background: valeur && !occupe ? C.green : C.border,
                color: '#fff',
              }}
              onClick={confirme}
            >
              {occupe ? tr('Un instant…') : tr('✓ Enregistrer')}
            </button>
          </div>
        )}
        {offreChoisie?.statut === 'En attente' && (
          <div style={{ ...petit, color: C.muted }}>
            {trf(
              'L’offre n° {0} sera marquée acceptée : l’acompte payé vaut accord.',
              offreChoisie.num || offreChoisie.id,
            )}
          </div>
        )}
        {!rien && ailleurs}
      </>
    );
  };

  // La dépense d'une sortie du relevé, telle que « ➕ Ajouter en dépense » la note.
  const depenseDe = (t) => {
    // Le commerce et l'endroit de l'achat, lus dans le libellé : « Café du
    // Simplon — Rue du Simplon 12, 1006 Lausanne ».
    const { nom, lieu } = nomEtLieu(t.label);
    return {
      type: categorieDe(t),
      libelle: t.label || tr('Paiement bancaire'),
      fournisseur: nom && lieu ? nom + ' — ' + lieu : nom || commerceDuLibelle(t.label),
      montant: +t.montant || 0,
      date: jour(t),
      // Payé à l'étranger : la TVA d'un autre pays ne se récupère pas en Suisse.
      ...(achatALEtranger(t.label, t.devise) ? { montantTva: 0 } : {}),
      piece: '',
      pieceNom: '',
      pieceMime: '',
    };
  };
  // Les sorties que l'écran sait ranger seul (café, restaurant, carburant…),
  // pas encore en dépense, ni en double possible, ni un retrait : le patron
  // devait cliquer « Ajouter en dépense » ligne après ligne, et un relevé de
  // cinquante cafés ne passait jamais dans la comptabilité. Une sortie du même
  // montant qu'une dépense déjà saisie (peut-être la même) reste à trancher.
  const reconnues = onAjouteDepense
    ? mouvements.filter(
        (t, i) =>
          t.sens === 'DBIT' &&
          !t.traite &&
          !enAttente(t) &&
          !liens[i] &&
          !estUnRetrait(t.label) &&
          !!categorieDe(t),
      )
    : [];
  const totalReconnues = reconnues.reduce((s, t) => s + (+t.montant || 0), 0);
  const ajouteLesReconnues = () => {
    const message = trf(
      'Ajouter {0} sorties du relevé en dépense, pour {1} ?',
      reconnues.length,
      francs(totalReconnues),
    );
    if (typeof window !== 'undefined' && window.confirm && !window.confirm(message)) return;
    reconnues.forEach((t) => onAjouteDepense(depenseDe(t)));
  };

  return (
    <Card>
      <div style={{ padding: 12 }}>
        {reconnues.length > 1 && (
          <div
            style={{
              background: C.redL,
              borderRadius: 9,
              padding: '8px 10px',
              marginBottom: 8,
              fontSize: 10,
              color: C.ink,
            }}
          >
            <div style={{ marginBottom: 5 }}>
              {trf(
                '{0} sorties reconnues (cafés, restaurants, carburant…) ne sont pas encore dans tes dépenses : {1}.',
                reconnues.length,
                francs(totalReconnues),
              )}
            </div>
            <button
              type="button"
              style={{ ...bouton, background: C.red, color: '#fff' }}
              onClick={ajouteLesReconnues}
            >
              {trf('➕ Tout ajouter en dépense ({0})', reconnues.length)}
            </button>
          </div>
        )}
        {mouvements.map((t, i) => {
          const lien = liens[i];
          const regle =
            lien?.genre === 'comptable' && comptable?.paye?.[lien.annee + '-' + lien.periode];
          const entree = t.sens === 'CRDT' && !t.traite && !enAttente(t) ? entrees[i] : null;
          // Le commerçant et son lieu en titre ; le libellé complet dessous,
          // sur plusieurs lignes : coupé à 11 px, le commerçant disparaissait.
          const titre = commerceDuLibelle(t.label) || t.label || tr('Mouvement bancaire');
          const libelleComplet = String(t.label || '').trim();
          const autre = t.doublonPossible
            ? mouvements.find((m) => m !== t && cle(m) === t.doublonPossible)
            : null;
          const retrait = t.sens === 'DBIT' && estUnRetrait(t.label);
          const annule = onNote && (
            <button
              type="button"
              style={lienDiscret}
              onClick={() => onNote(cle(t), { traite: undefined })}
            >
              {tr('Annuler')}
            </button>
          );
          return (
            <div
              key={i}
              data-testid="mouvement"
              style={{ padding: '8px 0', borderBottom: '1px solid #f0f2f5', fontSize: 11 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    data-testid="mouvement-titre"
                    style={{ fontWeight: 700, color: C.ink, overflowWrap: 'anywhere' }}
                  >
                    {titre}
                  </div>
                  <div style={{ fontSize: 8.5, color: C.muted }}>
                    {libelleNature(t) + ' · ' + isoVersDate(jourIso(t.date))}
                    {t.ref ? ' · ' + t.ref.slice(0, 18) : ''}
                  </div>
                  {libelleComplet && libelleComplet !== titre && (
                    <div
                      data-testid="mouvement-libelle"
                      style={{
                        fontSize: 9,
                        color: C.muted,
                        lineHeight: 1.35,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        marginTop: 1,
                      }}
                    >
                      {libelleComplet}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontWeight: 800,
                    color: t.sens === 'CRDT' ? C.green : t.sens === 'DBIT' ? C.red : C.amber,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {(t.sens === 'CRDT' ? '+' : t.sens === 'DBIT' ? '−' : '? ') + fmt(t.montant)}
                </div>
              </div>
              {!sensConnu(t) && (
                <div style={{ ...petit, color: C.amber, fontWeight: 700 }}>
                  {tr(
                    '⚠ Sens à vérifier : le relevé ne dit pas si c’est une entrée ou une sortie.',
                  )}
                  {onNote && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        style={{ ...bouton, background: C.greenL, color: C.green }}
                        onClick={() => onNote(cle(t), noteDuSens(t, 'CRDT'))}
                      >
                        {'⬇ ' + tr('Entrée')}
                      </button>
                      <button
                        type="button"
                        style={{ ...bouton, background: C.redL, color: C.red }}
                        onClick={() => onNote(cle(t), noteDuSens(t, 'DBIT'))}
                      >
                        {'⬆ ' + tr('Sortie')}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {t.doublonPossible && (
                <div style={{ ...petit, color: C.amber, fontWeight: 700 }}>
                  {trf(
                    '⚠ Déjà dans un autre relevé ? {0} — retire le doublon, ou garde les deux.',
                    autre
                      ? isoVersDate(jourIso(autre.date)) +
                          ' · ' +
                          (commerceDuLibelle(autre.label) || autre.label || '')
                      : '',
                  )}
                  {onNote && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {onRetire && (
                        <button
                          type="button"
                          style={{ ...bouton, background: C.redL, color: C.red }}
                          onClick={() => onRetire(cle(t))}
                        >
                          {'🗑 ' + tr('Retirer')}
                        </button>
                      )}
                      <button
                        type="button"
                        style={{
                          ...bouton,
                          background: 'none',
                          color: C.muted,
                          border: '1px solid ' + C.border,
                        }}
                        onClick={() => onNote(cle(t), { doublonPossible: undefined })}
                      >
                        {'✓ ' + tr('Les deux')}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {lien?.genre === 'comptable' &&
                (regle ? (
                  <div style={{ ...petit, color: C.green, fontWeight: 700 }}>
                    {trf(
                      '✓ Paiement du comptable pointé — {0}',
                      libellePeriode(comptable.freq, lien.periode, lien.annee),
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    style={{ ...bouton, background: C.tealL, color: C.teal }}
                    onClick={() => onPointeComptable(lien.annee, lien.periode)}
                  >
                    {trf(
                      '✓ C’est le comptable : pointer {0}',
                      libellePeriode(comptable.freq, lien.periode, lien.annee),
                    )}
                  </button>
                ))}
              {lien?.genre === 'fournisseur' && !lien.incertain && (
                <div style={{ ...petit, color: C.green, fontWeight: 700 }}>
                  {trf(
                    '✓ Facture fournisseur {0} · {1} — déjà comptée',
                    lien.fournisseur,
                    lien.chantier,
                  )}
                </div>
              )}
              {lien?.incertain && (
                <div style={{ ...petit, color: C.green, fontWeight: 700 }}>
                  {trf('✓ Déjà comptée : {0} (même montant)', lien.libelle)}
                </div>
              )}
              {lien?.genre === 'soustraitant' && !lien.incertain && (
                <div style={{ ...petit, color: C.green, fontWeight: 700 }}>
                  {trf('✓ Sous-traitant {0} · {1} — déjà compté', lien.fournisseur, lien.chantier)}
                </div>
              )}
              {lien?.genre === 'piece' && !lien.incertain && (
                <div style={{ ...petit, color: C.green, fontWeight: 700 }}>
                  {trf(
                    '✓ Facture {0} jointe à une offre ou une facture — déjà comptée',
                    lien.fournisseur,
                  )}
                </div>
              )}
              {lien?.genre === 'salaire' && (
                <div style={{ ...petit, color: C.green, fontWeight: 700 }}>
                  {lien.nom
                    ? trf('✓ Salaire de {0} — déjà compté dans les salaires (Équipe)', lien.nom)
                    : tr('✓ Salaire — déjà compté dans les salaires (Équipe)')}
                </div>
              )}
              {lien?.genre === 'depense' && !lien.incertain && !lien.propose && (
                <div style={{ ...petit, color: C.green, fontWeight: 700 }}>
                  {trf('✓ Déjà en dépense : {0}', lien.libelle)}
                </div>
              )}
              {t.sens === 'CRDT' && t.traite === 'ailleurs' && (
                <div style={{ ...petit, color: C.green, fontWeight: 700 }}>
                  {tr('✓ Facture d’un autre logiciel')}
                  {annule}
                </div>
              )}
              {t.sens === 'DBIT' && t.traite === 'prive' && (
                <div style={{ ...petit, color: C.green, fontWeight: 700 }}>
                  {tr('✓ Déjà comptée ou privée')}
                  {annule}
                </div>
              )}
              {entree && (
                <div
                  style={{
                    ...petit,
                    color: entree.facture.statut === 'Payée' ? C.green : C.amber,
                    fontWeight: 700,
                  }}
                >
                  {entree.facture.statut === 'Payée'
                    ? trf(
                        '✓ Facture n° {0} · {1} — payée',
                        entree.facture.num || entree.facture.id,
                        nomClient(entree.facture.clientId),
                      )
                    : entree.par === 'main'
                      ? // Le paiement est déjà noté (acompte en partie, par
                        // exemple) : pas de second bouton qui le compterait deux fois.
                        trf(
                          '✓ Paiement enregistré — facture n° {0} · {1}',
                          entree.facture.num || entree.facture.id,
                          nomClient(entree.facture.clientId),
                        )
                      : trf(
                          'Paiement de la facture n° {0} · {1}',
                          entree.facture.num || entree.facture.id,
                          nomClient(entree.facture.clientId),
                        )}
                  {entree.facture.statut !== 'Payée' && entree.par !== 'main' && onPaiement && (
                    <button
                      type="button"
                      style={{ ...bouton, background: C.green, color: '#fff', marginLeft: 6 }}
                      onClick={() => {
                        onPaiement(entree.facture.id, +t.montant || 0, jour(t));
                        lie(t, entree.facture.id);
                      }}
                    >
                      {tr('✓ Enregistrer le paiement')}
                    </button>
                  )}
                </div>
              )}
              {t.sens === 'CRDT' &&
                !t.traite &&
                !t.doublonPossible &&
                !entree &&
                onPaiement &&
                lignePaiement(t)}
              {(!lien || lien.propose) &&
                t.sens === 'DBIT' &&
                !t.traite &&
                !t.doublonPossible &&
                lien?.propose && (
                  // Une dépense du même montant, que rien n'assure : proposée,
                  // jamais liée d'office (elle en datait payée une autre).
                  <div style={{ ...petit, color: C.amber, fontWeight: 700 }}>
                    {trf(
                      'Même montant que la dépense « {0} » du {1} : déjà comptée ?',
                      lien.libelle,
                      isoVersDate(jourIso(lien.date)) || lien.date,
                    )}
                  </div>
                )}
              {t.sens === 'DBIT' && !retrait && achatALEtranger(t.label, t.devise) && (
                <div style={{ ...petit, color: C.muted }}>
                  {tr('🌍 Achat à l’étranger : pas de TVA suisse à récupérer.')}
                </div>
              )}
              {retrait && !t.traite && !t.doublonPossible && (
                <div style={{ ...petit, color: C.muted }}>
                  {tr(
                    'Retrait d’espèces : pas une dépense. Les achats payés avec cet argent se notent à part.',
                  )}
                </div>
              )}
              {(!lien || lien.propose) &&
                t.sens === 'DBIT' &&
                !t.traite &&
                !t.doublonPossible &&
                onAjouteDepense && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    {!retrait && (
                      <select
                        aria-label={tr('Catégorie de la dépense')}
                        value={categorieDe(t)}
                        onChange={(e) => setCategorie((p) => ({ ...p, [cle(t)]: e.target.value }))}
                        style={{
                          marginTop: 5,
                          border: '1px solid ' + C.border,
                          borderRadius: 7,
                          padding: '5px 6px',
                          fontSize: 9.5,
                          maxWidth: '55%',
                        }}
                      >
                        {!categorieDe(t) && (
                          <option value="" disabled>
                            {tr('— Choisis la catégorie —')}
                          </option>
                        )}
                        {/* Par ordre alphabétique : « Loyer » (le dépôt), « Transports »…
                        se trouvent sans parcourir toute la liste. */}
                        {categoriesTriees.map(([k, v]) => (
                          <option key={k} value={k}>
                            {tr(v.lib)}
                          </option>
                        ))}
                      </select>
                    )}
                    {!retrait && (
                      <button
                        type="button"
                        disabled={!categorieDe(t)}
                        style={{
                          ...bouton,
                          background: C.redL,
                          color: C.red,
                          opacity: categorieDe(t) ? 1 : 0.45,
                        }}
                        onClick={() => onAjouteDepense(depenseDe(t))}
                      >
                        {tr('➕ Ajouter en dépense')}
                      </button>
                    )}
                    {/* Une sortie déjà comptée ailleurs (l'ancien logiciel) ou
                      privée : rien à ajouter, la ligne ne se repropose plus. */}
                    {onNote && (
                      <button
                        type="button"
                        style={{
                          ...bouton,
                          background: 'none',
                          color: C.muted,
                          border: '1px solid ' + C.border,
                        }}
                        onClick={() => onNote(cle(t), { traite: 'prive' })}
                      >
                        {tr('🚫 Déjà comptée / privée')}
                      </button>
                    )}
                  </div>
                )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
